// Prescribing alerts (docs/plans/phase1-consultation.md §4.1).
//
// Three tiers only (CLAUDE.md): blocking (cannot sign), interruptive (acknowledge with a
// reason, logged with the rule ID), passive (a marker beside the field). Every alert names a
// stable rule ID so override rates can be measured per rule. The server's evaluation is the
// one stored; the client never decides a tier.
//
// Alerts carry codes, numbers and record IDs only. Words are composed on the screen from
// these facts, so a stored snapshot never holds free text a patient or clinician typed.

import type { ClinicalRuleApproval } from '../clinical-rules/rules.ts';

export const PRESCRIBING_RULE_IDS = [
  'rx.allergy.severe',
  'rx.allergy.match',
  'rx.allergy.uncoded',
  'rx.allergy.not_matchable',
  'rx.allergy.not_recorded',
  'rx.duplicate.ingredient',
  'rx.duplicate.group',
  'rx.dose.max_single',
  'rx.dose.max_daily',
  'rx.dose.per_kg_high',
  'rx.dose.per_kg_low',
  'rx.dose.weight_band',
  'rx.dose.no_weight',
  'rx.dose.cannot_check',
  'rx.dose.no_rule',
  'rx.estimated_age',
  'rx.formulary.not_stocked',
  'rx.quantity_short',
] as const;
export type PrescribingRuleId = (typeof PRESCRIBING_RULE_IDS)[number];

export const ALERT_TIERS = ['blocking', 'interruptive', 'passive'] as const;
export type AlertTier = (typeof ALERT_TIERS)[number];

/**
 * The version of the built-in checks' logic (allergy, duplicate, cannot-check). Dose alerts
 * carry the version of the tenant's dose rule instead. Bump when the logic changes, so a
 * stored alert says which logic judged it.
 */
export const BUILT_IN_RULE_VERSION = 1;

export type AlertFact = string | number | boolean | null;

export interface PrescribingAlert {
  readonly ruleId: PrescribingRuleId;
  readonly tier: AlertTier;
  /** The draft line the alert is about; null when it is about the whole prescription. */
  readonly lineKey: string | null;
  readonly ruleVersion: number;
  /** The tenant's dose rule that judged, when one did. */
  readonly doseRule: {
    readonly id: string;
    readonly version: number;
    readonly approval: ClinicalRuleApproval;
    readonly source: string;
  } | null;
  /** True when a provisional rule or group membership produced the alert. */
  readonly provisional: boolean;
  /** IDs of the records the alert rests on: allergies, memberships, lines, observations. */
  readonly evidence: readonly string[];
  /** Codes and numbers the message interpolates. Never free text typed by a person. */
  readonly facts: Readonly<Record<string, AlertFact>>;
}

const TIER_ORDER: Record<AlertTier, number> = { blocking: 0, interruptive: 1, passive: 2 };

/**
 * A stable order: tier, then rule ID, then line (prescription-level first), then the
 * order found. The panel never reorders between two checks of the same input.
 */
export function sortAlerts(alerts: readonly PrescribingAlert[]): PrescribingAlert[] {
  return alerts
    .map((alert, index) => ({ alert, index }))
    .sort((a, b) => {
      const tier = TIER_ORDER[a.alert.tier] - TIER_ORDER[b.alert.tier];
      if (tier !== 0) return tier;
      if (a.alert.ruleId !== b.alert.ruleId) return a.alert.ruleId < b.alert.ruleId ? -1 : 1;
      const lineA = a.alert.lineKey ?? '';
      const lineB = b.alert.lineKey ?? '';
      if (lineA !== lineB) return lineA < lineB ? -1 : 1;
      return a.index - b.index;
    })
    .map(({ alert }) => alert);
}

/** How an acknowledgement names what it answers: one rule on one line (or the prescription). */
export function alertKey(ruleId: string, lineKey: string | null): string {
  return `${ruleId}|${lineKey ?? '*'}`;
}

export interface AlertKey {
  readonly ruleId: PrescribingRuleId;
  readonly lineKey: string | null;
}

function uniqueKeys(alerts: readonly PrescribingAlert[], tier: AlertTier): AlertKey[] {
  const seen = new Set<string>();
  const keys: AlertKey[] = [];
  for (const alert of alerts) {
    if (alert.tier !== tier) continue;
    const key = alertKey(alert.ruleId, alert.lineKey);
    if (seen.has(key)) continue;
    seen.add(key);
    keys.push({ ruleId: alert.ruleId, lineKey: alert.lineKey });
  }
  return keys;
}

/** The interruptive alerts that need an acknowledgement with a reason before signing. */
export function requiredAcknowledgements(alerts: readonly PrescribingAlert[]): AlertKey[] {
  return uniqueKeys(alerts, 'interruptive');
}

/** The blocking alerts; while any stands, the prescription cannot be signed. */
export function blockingKeys(alerts: readonly PrescribingAlert[]): AlertKey[] {
  return uniqueKeys(alerts, 'blocking');
}
